import { User } from '@supabase/supabase-js';

interface UserProfile {
  id: string;
  email: string;
  name: string;
  organizationId: string;
  organizationRole: string;
  companyName?: string;
  companySize?: string;
  companyIndustry?: string;
  companyDescription?: string;
  onboardingCompleted: boolean;
  themePreference?: 'light' | 'dark';
  notificationPreferences?: Record<string, boolean>;
  createdAt: string;
  phone?: string;
  city?: string;
  country?: string;
  location?: string;
  bio?: string;
  website?: string;
  timezone?: string;
  avatar?: string;
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  marketingGoals?: string[];
  updatedAt?: string;
}

// Debounce tracking calls to prevent spam
let lastUserPropertiesCall = 0;
let lastUserEventCall = 0;
const DEBOUNCE_DELAY = 1000; // 1 second

// Generate a consistent, privacy-compliant user ID
export function generateUserTrackingId(userUuid: string): string {
  // Create a hash of the UUID for privacy compliance
  // This creates a consistent but non-reversible identifier
  const hash = btoa(userUuid).replace(/[^a-zA-Z0-9]/g, '').substring(0, 16);
  return `user_${hash}`;
}

// Generate a numeric user ID for analytics (optional)
export function generateNumericUserId(userUuid: string): number {
  let hash = 0;
  for (let i = 0; i < userUuid.length; i++) {
    const char = userUuid.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Get user context for tracking
export function getUserTrackingContext(user: User | null, userProfile: UserProfile | null) {
  if (!user) {
    return {
      user_id: null,
      user_type: 'anonymous',
      user_numeric_id: null,
      is_authenticated: false
    };
  }

  const trackingId = generateUserTrackingId(user.id);
  const numericId = generateNumericUserId(user.id);

  return {
    user_id: trackingId,
    user_type: 'authenticated',
    user_numeric_id: numericId,
    is_authenticated: true,
    user_email_domain: user.email ? user.email.split('@')[1] : null,
    user_created_at: user.created_at ? new Date(user.created_at).toISOString().split('T')[0] : null,
    subscription_status: userProfile?.organizationRole || user.user_metadata?.subscription_status || 'free',
    onboarding_completed: userProfile?.onboardingCompleted || user.user_metadata?.onboarding_completed || false
  };
}

// Enhanced tracking function with user context
export function pushGTMEventWithUser({
  elementType,
  elementId,
  elementText,
  pageName,
  section,
  variant,
  userAction,
  customProperties = {},
  user = null,
  userProfile = null
}: {
  elementType: string;
  elementId: string;
  elementText?: string;
  pageName: string;
  section?: string;
  variant?: string;
  userAction: 'click' | 'focus' | 'blur' | 'change' | 'submit' | 'hover' | 'scroll' | 'view';
  customProperties?: Record<string, any>;
  user?: User | null;
  userProfile?: UserProfile | null;
}) {
  const userContext = getUserTrackingContext(user, userProfile);
  
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    const event = {
      event: 'user_interaction',
      element_type: elementType,
      element_id: elementId,
      element_text: elementText,
      page_name: pageName,
      section,
      variant,
      user_action: userAction,
      timestamp: Date.now(),
      // User context
      ...userContext,
      // Custom properties
      ...customProperties
    };
    
    window.dataLayer.push(event);
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('GTM Event with User Context:', event);
    }
  }
}

// Set user properties in GTM (call on login/user change) with debouncing
export function setGTMUserProperties(user: User | null = null, userProfile: UserProfile | null = null) {
  const now = Date.now();
  
  // Debounce to prevent rapid successive calls
  if (now - lastUserPropertiesCall < DEBOUNCE_DELAY) {
    console.log('GTM User Properties call debounced');
    return;
  }
  lastUserPropertiesCall = now;
  
  // Try to get user from auth context if not provided
  let contextUser = user;
  let contextUserProfile = userProfile;
  
  // If no user provided, try to get from window context (set by auth provider)
  if (!contextUser && typeof window !== 'undefined') {
    contextUser = (window as any).__CRUTAN_USER__ || null;
    contextUserProfile = (window as any).__CRUTAN_USER_PROFILE__ || null;
  }
  
  const userContext = getUserTrackingContext(contextUser, contextUserProfile);
  
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    // Set user properties
    window.dataLayer.push({
      event: 'user_properties_set',
      ...userContext
    });
    
    // Set GA4 user properties
    if (window.gtag) {
      window.gtag('config', 'G-W7QTL9BLW0', {
        user_id: userContext.user_id,
        custom_map: {
          user_type: userContext.user_type,
          subscription_status: userContext.subscription_status
        }
      });
    }
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log('GTM User Properties Set:', userContext);
    }
  }
}

// Track user authentication events with debouncing
export function trackUserEvent(
  eventType: 'login' | 'logout' | 'signup' | 'subscription_change', 
  additionalData = {},
  user: User | null = null,
  userProfile: UserProfile | null = null
) {
  const now = Date.now();
  
  // Only debounce for certain event types to prevent spam
  if ((eventType === 'login' || eventType === 'logout') && now - lastUserEventCall < DEBOUNCE_DELAY) {
    console.log(`GTM User Event (${eventType}) call debounced`);
    return;
  }
  lastUserEventCall = now;
  
  // Try to get user from auth context if not provided
  let contextUser = user;
  let contextUserProfile = userProfile;
  
  // If no user provided, try to get from window context (set by auth provider)
  if (!contextUser && typeof window !== 'undefined') {
    contextUser = (window as any).__CRUTAN_USER__ || null;
    contextUserProfile = (window as any).__CRUTAN_USER_PROFILE__ || null;
  }
  
  const userContext = getUserTrackingContext(contextUser, contextUserProfile);
  
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    
    const event = {
      event: 'user_event',
      event_type: eventType,
      ...userContext,
      ...additionalData,
      timestamp: Date.now()
    };
    
    window.dataLayer.push(event);
    
    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`GTM User Event (${eventType}):`, event);
    }
  }
}

// Helper function to set user context in window for utility functions
export function setUserContextInWindow(user: User | null, userProfile: UserProfile | null) {
  if (typeof window !== 'undefined') {
    (window as any).__CRUTAN_USER__ = user;
    (window as any).__CRUTAN_USER_PROFILE__ = userProfile;
  }
}

// Declare global gtag for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
    __CRUTAN_USER__?: User | null;
    __CRUTAN_USER_PROFILE__?: UserProfile | null;
  }
} 